import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clipboard,
  Headphones,
  KeyRound,
  Loader2,
  LogOut,
  RefreshCw,
  RotateCcw,
  Save,
  Upload,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import ThemeToggle from '../components/ThemeToggle';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_LABELS = {
  not_started: 'Sin iniciar',
  raw_received: 'Crudo recibido',
  auto_processing: 'Procesando automatico',
  auto_processed: 'Procesado automatico',
  editing: 'En edición',
  ready_for_review: 'Listo para revisar',
  approved: 'Aprobado iOS',
  changes_requested: 'Cambios pedidos',
  error: 'Error',
};

const AUDIO_KINDS = [
  { kind: 'beginner', label: 'Principiante 1' },
  { kind: 'beginner-alt', label: 'Principiante 2' },
  { kind: 'edited', label: 'Advanced final' },
];

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getKindState = (workflow, kind) => {
  if (!workflow) return null;
  if (kind === 'edited') return workflow.mobile_audio?.advanced || null;
  return (workflow.mobile_audio?.beginner || []).find((item) => item.kind === kind) || null;
};

export function AdminAudioDashboard({ token, user, onLogout, onBackToAdmin }) {
  const isAdmin = user?.role !== 'editor';
  const [workflows, setWorkflows] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [editorUsers, setEditorUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState(isAdmin ? 'all' : 'available');
  const [search, setSearch] = useState('');
  const [files, setFiles] = useState({});
  const [pendingAction, setPendingAction] = useState('');
  const [mobileCredential, setMobileCredential] = useState(null);
  const [changeNote, setChangeNote] = useState('');
  const [newEditor, setNewEditor] = useState({ username: '', name: '', password: '' });

  const selectedSummary = useMemo(
    () => workflows.find((item) => item.id === selectedId) || selectedWorkflow,
    [selectedId, selectedWorkflow, workflows]
  );

  const loadWorkflows = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '300');
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (assignedFilter !== 'all' || !isAdmin) params.set('assigned', assignedFilter);
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(`${API_BASE}/api/admin/audio-workflows?${params}`, {
        credentials: 'include',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('No se pudo cargar la cola de audio');
      const data = await res.json();
      setWorkflows(data.workflows || []);
      if (!selectedId && data.workflows?.length) setSelectedId(data.workflows[0].id);
    } catch (error) {
      toast.error(error.message || 'Error cargando audios');
    } finally {
      setIsLoading(false);
    }
  }, [assignedFilter, isAdmin, search, selectedId, statusFilter, token]);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setSelectedWorkflow(null);
      return;
    }
    setIsDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/audio-workflows/${id}`, {
        credentials: 'include',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('No se pudo cargar el detalle');
      const data = await res.json();
      setSelectedWorkflow(data.workflow);
    } catch (error) {
      toast.error(error.message || 'Error cargando detalle');
    } finally {
      setIsDetailLoading(false);
    }
  }, [token]);

  const loadEditorUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/editor-users`, {
        credentials: 'include',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('No se pudieron cargar editores');
      const data = await res.json();
      setEditorUsers(data.editor_users || []);
    } catch (error) {
      toast.error(error.message || 'Error cargando editores');
    }
  }, [isAdmin, token]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    loadEditorUsers();
  }, [loadEditorUsers]);

  useEffect(() => {
    loadDetail(selectedId);
    setMobileCredential(null);
  }, [loadDetail, selectedId]);

  const replaceWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setWorkflows((prev) => prev.map((item) => (item.id === workflow.id ? workflow : item)));
  };

  const postWorkflowAction = async (path, options = {}) => {
    if (!selectedId) return;
    setPendingAction(path);
    try {
      const res = await fetch(`${API_BASE}/api/admin/audio-workflows/${selectedId}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...authHeaders(token),
          ...(options.json ? { 'Content-Type': 'application/json' } : {}),
        },
        body: options.json ? JSON.stringify(options.json) : options.body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'No se pudo completar la acción');
      if (data.workflow) replaceWorkflow(data.workflow);
      return data;
    } catch (error) {
      toast.error(error.message || 'Error');
      return null;
    } finally {
      setPendingAction('');
    }
  };

  const handleAssign = async (editorUserId) => {
    const payload = { editor_user_id: editorUserId === 'none' ? null : editorUserId };
    const data = await postWorkflowAction('/assign', { json: payload });
    if (data) toast.success(editorUserId === 'none' ? 'Audio sin editor asignado' : 'Editor asignado');
  };

  const handleTakeMine = async () => {
    const data = await postWorkflowAction('/assign', { json: { editor_user_id: user?.editor_user_id || null } });
    if (data) toast.success('Audio tomado');
  };

  const handleUpload = async (kind) => {
    const file = files[kind];
    if (!file) {
      toast.error('Seleccioná un archivo');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    const data = await postWorkflowAction(`/upload/${kind}`, { body: formData });
    if (data) {
      setFiles((prev) => ({ ...prev, [kind]: null }));
      toast.success('Audio subido para revisión');
    }
  };

  const handleApprove = async (kind) => {
    const data = await postWorkflowAction(`/approve/${kind}`);
    if (data) toast.success('Audio aprobado para app');
  };

  const handleRevoke = async (kind) => {
    const data = await postWorkflowAction(`/revoke/${kind}`);
    if (data) toast.success('Audio revocado');
  };

  const handleRequestChanges = async () => {
    const data = await postWorkflowAction('/request-changes', { json: { note: changeNote } });
    if (data) {
      setChangeNote('');
      toast.success('Cambios registrados');
    }
  };

  const handleRotateToken = async () => {
    const data = await postWorkflowAction('/rotate-token');
    if (data?.token) {
      setMobileCredential({ slug: data.slug, token: data.token });
      toast.success('Token iOS generado');
    }
  };

  const handleCopyCredential = async () => {
    if (!mobileCredential) return;
    await navigator.clipboard.writeText(`slug=${mobileCredential.slug}\ntoken=${mobileCredential.token}`);
    toast.success('Credencial copiada');
  };

  const handleCreateEditor = async (event) => {
    event.preventDefault();
    setPendingAction('create-editor');
    try {
      const res = await fetch(`${API_BASE}/api/admin/editor-users`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(newEditor),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear editor');
      setEditorUsers((prev) => [data.editor_user, ...prev]);
      setNewEditor({ username: '', name: '', password: '' });
      toast.success('Editor creado');
    } catch (error) {
      toast.error(error.message || 'Error creando editor');
    } finally {
      setPendingAction('');
    }
  };

  const handleToggleEditor = async (editor) => {
    setPendingAction(`editor-${editor._id}`);
    try {
      const res = await fetch(`${API_BASE}/api/admin/editor-users/${editor._id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !editor.active }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar editor');
      setEditorUsers((prev) => prev.map((item) => (item._id === editor._id ? data.editor_user : item)));
    } catch (error) {
      toast.error(error.message || 'Error actualizando editor');
    } finally {
      setPendingAction('');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(token),
      });
    } catch (error) {
      // ignore logout network errors
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    onLogout();
  };

  const renderStatusBadge = (status) => {
    const label = STATUS_LABELS[status] || status || 'Sin estado';
    const classes = {
      approved: 'border-green-300 bg-green-50 text-green-700',
      ready_for_review: 'border-amber-300 bg-amber-50 text-amber-700',
      editing: 'border-cyan-300 bg-cyan-50 text-cyan-700',
      changes_requested: 'border-red-300 bg-red-50 text-red-700',
    };
    return <Badge variant="outline" className={classes[status] || ''}>{label}</Badge>;
  };

  const renderKindCard = (kindConfig) => {
    const state = getKindState(selectedWorkflow, kindConfig.kind);
    const ready = state?.ready && state?.status === 'approved';
    const fileUrl = selectedWorkflow
      ? `${API_BASE}/api/admin/audio-workflows/${selectedWorkflow.id}/file?kind=${kindConfig.kind}`
      : '';
    return (
      <Card key={kindConfig.kind}>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">{kindConfig.label}</CardTitle>
              <CardDescription>{state?.file_name || 'Sin archivo cargado'}</CardDescription>
            </div>
            <Badge variant={ready ? 'default' : 'outline'}>
              {ready ? 'Listo app' : state?.status || 'Pendiente'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {state?.file_key && <audio controls src={fileUrl} className="w-full" />}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="file"
              accept="audio/*"
              onChange={(event) => setFiles((prev) => ({ ...prev, [kindConfig.kind]: event.target.files?.[0] || null }))}
            />
            <Button type="button" onClick={() => handleUpload(kindConfig.kind)} disabled={pendingAction === `/upload/${kindConfig.kind}`}>
              {pendingAction === `/upload/${kindConfig.kind}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Subir
            </Button>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => handleApprove(kindConfig.kind)} disabled={!state?.file_key || pendingAction === `/approve/${kindConfig.kind}`}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Aprobar
              </Button>
              <Button type="button" variant="outline" onClick={() => handleRevoke(kindConfig.kind)} disabled={!state?.file_key || pendingAction === `/revoke/${kindConfig.kind}`}>
                <XCircle className="w-4 h-4 mr-2" />
                Revocar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBackToAdmin && (
              <Button type="button" variant="ghost" onClick={onBackToAdmin}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <div>
              <h1 className="text-xl font-bold text-primary">Audio para app iOS</h1>
              <p className="text-sm text-muted-foreground">
                {user?.role === 'editor' ? `Editor: ${user.name || user.username}` : 'Cola interna de audio'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="cola">
          <TabsList>
            <TabsTrigger value="cola" className="gap-2">
              <Headphones className="w-4 h-4" />
              Cola de audio
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="editores" className="gap-2">
                <Users className="w-4 h-4" />
                Editores
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="cola" className="mt-6">
            <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Audios del formulario</CardTitle>
                    <Button type="button" variant="outline" size="sm" onClick={loadWorkflows}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardDescription>
                    {isAdmin
                      ? 'Formularios con audio crudo recibido.'
                      : 'Audios disponibles y trabajos tomados por vos.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Input placeholder="Buscar alumno..." value={search} onChange={(event) => setSearch(event.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="raw_received">Crudo recibido</SelectItem>
                          <SelectItem value="auto_processing">Procesando</SelectItem>
                          <SelectItem value="auto_processed">Procesados</SelectItem>
                          <SelectItem value="editing">En edición</SelectItem>
                          <SelectItem value="ready_for_review">Para revisar</SelectItem>
                          <SelectItem value="approved">Aprobados</SelectItem>
                          <SelectItem value="changes_requested">Cambios</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {isAdmin ? (
                            <SelectItem value="all">Todos</SelectItem>
                          ) : (
                            <SelectItem value="available">Disponibles</SelectItem>
                          )}
                          <SelectItem value="unassigned">Sin editor</SelectItem>
                          <SelectItem value="mine">Mis audios</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[660px] overflow-y-auto pr-1">
                    {isLoading ? (
                      <div className="py-10 text-center text-muted-foreground">
                        <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
                        Cargando...
                      </div>
                    ) : workflows.length === 0 ? (
                      <div className="py-10 text-center text-sm text-muted-foreground">No hay audios para este filtro.</div>
                    ) : workflows.map((workflow) => (
                      <button
                        key={workflow.id}
                        type="button"
                        onClick={() => setSelectedId(workflow.id)}
                        className={`w-full text-left rounded-md border p-3 transition hover:bg-muted/60 ${selectedId === workflow.id ? 'border-primary bg-primary/5' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium">{workflow.user?.name || '-'}</div>
                            <div className="text-xs text-muted-foreground">{workflow.slug}</div>
                          </div>
                          {renderStatusBadge(workflow.status)}
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          Editor: {workflow.assigned_editor?.name || 'Sin asignar'} · Raw v{workflow.raw_version || 0}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                {!selectedSummary ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      Seleccioná un audio de la cola.
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <Card>
                      <CardHeader>
                        <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                          <div>
                            <CardTitle>{selectedSummary.user?.name}</CardTitle>
                            <CardDescription>{selectedSummary.user?.email} · {selectedSummary.slug}</CardDescription>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {renderStatusBadge(selectedSummary.status)}
                            <Badge variant="outline">Raw v{selectedSummary.raw_version || 0}</Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        {isDetailLoading ? (
                          <div className="py-8 text-center text-muted-foreground">
                            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
                            Cargando detalle...
                          </div>
                        ) : (
                          <>
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="rounded-md border p-4 space-y-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div>
                                    <div className="font-semibold">Audio crudo</div>
                                    <div className="text-xs text-muted-foreground">{selectedWorkflow?.raw_audio?.file_name || 'Sin archivo'}</div>
                                  </div>
                                  <Badge variant="outline">{formatDate(selectedWorkflow?.raw_audio?.uploaded_at)}</Badge>
                                </div>
                                {selectedWorkflow?.raw_audio?.file_key ? (
                                  <audio controls src={`${API_BASE}/api/admin/audio-workflows/${selectedWorkflow.id}/file?kind=raw`} className="w-full" />
                                ) : (
                                  <div className="text-sm text-muted-foreground">Todavía no hay audio crudo.</div>
                                )}
                              </div>

                              <div className="rounded-md border p-4 space-y-3">
                                <div className="font-semibold">Editor asignado</div>
                                <div className="text-sm text-muted-foreground">
                                  {selectedWorkflow?.assigned_editor?.name || 'Sin asignar'}
                                </div>
                                {isAdmin ? (
                                  <Select
                                    value={selectedWorkflow?.assigned_editor?.editor_user_id || 'none'}
                                    onValueChange={handleAssign}
                                  >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Sin asignar</SelectItem>
                                      {editorUsers.filter((editor) => editor.active).map((editor) => (
                                        <SelectItem key={editor._id} value={editor._id}>{editor.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Button type="button" variant="outline" onClick={handleTakeMine}>
                                    <Save className="w-4 h-4 mr-2" />
                                    Tomar este audio
                                  </Button>
                                )}
                              </div>
                            </div>

                            {selectedWorkflow?.script?.full_text && (
                              <div className="rounded-md border p-4">
                                <div className="font-semibold mb-2">Texto grabado</div>
                                <div className="whitespace-pre-line text-sm leading-6 text-muted-foreground">
                                  {selectedWorkflow.script.full_text}
                                </div>
                              </div>
                            )}

                            <div className="grid gap-4 xl:grid-cols-3">
                              {AUDIO_KINDS.map(renderKindCard)}
                            </div>

                            {isAdmin && (
                              <Card>
                                <CardHeader>
                                  <CardTitle className="text-base">Credencial iOS</CardTitle>
                                  <CardDescription>
                                    El token se muestra solo al generarlo. iOS usa slug/token para consultar mobileAudio y reproducir archivos aprobados.
                                  </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                  <div className="flex flex-wrap gap-2">
                                    <Button type="button" variant="outline" onClick={handleRotateToken}>
                                      <KeyRound className="w-4 h-4 mr-2" />
                                      Generar token iOS
                                    </Button>
                                    {mobileCredential && (
                                      <Button type="button" onClick={handleCopyCredential}>
                                        <Clipboard className="w-4 h-4 mr-2" />
                                        Copiar slug/token
                                      </Button>
                                    )}
                                  </div>
                                  {mobileCredential ? (
                                    <div className="rounded-md border bg-muted/40 p-3 text-sm font-mono break-all">
                                      slug={mobileCredential.slug}<br />
                                      token={mobileCredential.token}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-muted-foreground">
                                      Token actual: {selectedWorkflow?.token_last4 ? `termina en ${selectedWorkflow.token_last4}` : 'no generado'}
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            )}

                            <Card>
                              <CardHeader>
                                <CardTitle className="text-base">Pedir cambios</CardTitle>
                                <CardDescription>Registra una nota interna y deja el workflow en cambios pedidos.</CardDescription>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <Textarea value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="Detalle para el editor..." rows={3} />
                                <Button type="button" variant="outline" onClick={handleRequestChanges}>
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Registrar cambios
                                </Button>
                              </CardContent>
                            </Card>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {selectedWorkflow?.audit_log?.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Historial</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {selectedWorkflow.audit_log.slice().reverse().slice(0, 12).map((event, index) => (
                            <div key={`${event.action}-${index}`} className="flex items-start gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                              <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                              <div>
                                <div className="font-medium">{event.action}</div>
                                <div className="text-xs text-muted-foreground">
                                  {event.actor?.name || 'Sistema'} · {formatDate(event.at)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="editores" className="mt-6">
              <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
                <Card>
                  <CardHeader>
                    <CardTitle>Nuevo editor</CardTitle>
                    <CardDescription>Usuarios con acceso limitado a la cola de audio.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateEditor} className="space-y-3">
                      <div className="space-y-2">
                        <Label>Usuario</Label>
                        <Input value={newEditor.username} onChange={(event) => setNewEditor((prev) => ({ ...prev, username: event.target.value }))} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Nombre visible</Label>
                        <Input value={newEditor.name} onChange={(event) => setNewEditor((prev) => ({ ...prev, name: event.target.value }))} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Contraseña</Label>
                        <Input type="password" value={newEditor.password} onChange={(event) => setNewEditor((prev) => ({ ...prev, password: event.target.value }))} required />
                      </div>
                      <Button type="submit" disabled={pendingAction === 'create-editor'}>
                        {pendingAction === 'create-editor' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                        Crear editor
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Editores</CardTitle>
                    <CardDescription>Activá o desactivá usuarios según el equipo.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {editorUsers.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Todavía no hay editores creados.</div>
                    ) : editorUsers.map((editor) => (
                      <div key={editor._id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                        <div>
                          <div className="font-medium">{editor.name}</div>
                          <div className="text-xs text-muted-foreground">{editor.username} · creado {formatDate(editor.created_at)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={editor.active ? 'default' : 'secondary'}>{editor.active ? 'Activo' : 'Inactivo'}</Badge>
                          <Button type="button" variant="outline" onClick={() => handleToggleEditor(editor)} disabled={pendingAction === `editor-${editor._id}`}>
                            {editor.active ? 'Desactivar' : 'Activar'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}

export default AdminAudioDashboard;
